import { useTranslation } from 'react-i18next';
import { Tooltip } from '@shape-ds/components';
import './style.scss';

export interface ILegendItem {
  label: string;
  color: string;
  enabled: boolean;
  description?: string;
  isDashed?: boolean;
  reference: string;
}

export interface ICustomAlarmItem {
  color?: string;
  label: string;
}

export type ILegend = {
  data: ILegendItem[];
  onClick?: (item: ILegendItem) => void;
  orientation?: 'fill' | 'horizontal' | 'vertical' | 'gap';
  hasAlarm?: boolean;
  hasClosedAlarm?: boolean;
  customAlarmMessage?: string;
  customAlarms?: ICustomAlarmItem[];
};

const availableElements = [
  {
    id: 'alarm',
    icon: (color = '#CC0000') => (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        className="mr-2"
        style={{
          minWidth: '16px',
        }}
      >
        <g clipPath="url(#clip0_4218_169177)">
          <rect width="16" height="16" fill="#372220" />
          <rect width="2" height="16" fill={color} />
        </g>
        <defs>
          <clipPath id="clip0_4218_169177">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    id: 'closed_alarm',
    icon: (color = '#2F5291') => (
      <svg
        width="2"
        height="16"
        viewBox="0 0 2 16"
        fill="none"
        className="mr-2"
        style={{
          width: '16px',
        }}
      >
        <g clipPath="url(#clip0_4218_169177)">
          <rect width="2" height="16" fill={color} />
        </g>
        <defs>
          <clipPath id="clip0_4218_169177">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    id: 'threshold',
    icon: (color?: string) => (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mr-2">
        <path d="M16 8H0" stroke={color} strokeWidth="2" strokeDasharray="4 2" />
      </svg>
    ),
  },
];
export const Legend = ({
  data,
  onClick,
  orientation = 'fill',
  hasAlarm = false,
  hasClosedAlarm = true,
  customAlarmMessage,
  customAlarms,
}: ILegend) => {
  const { t } = useTranslation();

  return (
    <ul className={`legend ${orientation}`}>
      {data.map((item, i) => (
        <li onClick={() => onClick?.(item)} key={`legend-item-${i + 1}`}>
          <div className={`item ${item.enabled ? '' : 'disabled'} ${onClick ? 'clickable' : ''}`}>
            {item.isDashed ? (
              availableElements.find(({ id }) => id === 'threshold')?.icon(item.color)
            ) : (
              <span className="is-block badge" style={{ background: item.color }} />
            )}
            {item.description ? (
              <Tooltip title={item.description}>
                <span className="label">{item.label}</span>
              </Tooltip>
            ) : (
              <span className="label">{item.label}</span>
            )}
          </div>
        </li>
      ))}

      {hasAlarm && (
        <>
          <li>
            <div className="pt-2 pb-2 item alarm-item">
              {availableElements.find(({ id }) => id === 'alarm')?.icon()}
              {customAlarmMessage ? (
                <span className="label">{customAlarmMessage}</span>
              ) : (
                <span className="label">{t('event_page:alarm_and_period')}</span>
              )}
            </div>
          </li>

          {hasClosedAlarm && (
            <li>
              <div className="pt-2 pb-2 item alarm-item">
                {availableElements.find(({ id }) => id === 'closed_alarm')?.icon()}
                <span className="label">{t('event_page:closed_alarm')}</span>
              </div>
            </li>
          )}
        </>
      )}

      {customAlarms?.map((alarm) => (
        <li key={alarm.label}>
          <div className="pt-2 pb-2 item alarm-item">
            {availableElements.find(({ id }) => id === 'alarm')?.icon(alarm.color)}
            <span className="label">{alarm.label}</span>
          </div>
        </li>
      ))}
    </ul>
  );
};
